import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Loader2, Building2, MapPin, LandPlot, Layers,
  Sparkles, FileStack, MessageSquare, Users, Calculator, Check, IndianRupee, CheckCircle2
} from "lucide-react";

const UNIT_TO_SQFT = { sqft: 1, sqyd: 9, sqm: 10.7639 };
const UNIT_LABEL = { sqft: "Sq Ft", sqyd: "Sq Yard", sqm: "Sq Metre" };
const COVERAGE = [50, 60, 70, 75, 90, 100];
const FLOORS = [1, 2, 3, 4, 5];
const BASEMENTS = [0, 1, 2, 3, 4];

const fmtSqft = (n) => `${Math.round(n).toLocaleString("en-IN")} Sq Ft`;

const emptyForm = {
  name: "", city: "", plot_area: "", plot_unit: "sqft",
  ground_coverage: 60, floors: 1, basement: 0, stage: "new", budget: "",
};

export default function ProjectWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const calc = useMemo(() => {
    const area = parseFloat(form.plot_area);
    if (!area || area <= 0) return null;
    const plotSqft = area * (UNIT_TO_SQFT[form.plot_unit] || 1);
    const gfa = plotSqft * (form.ground_coverage / 100);
    const above = gfa * form.floors;
    const base = gfa * form.basement;
    return { plotSqft, gfa, above, base, total: above + base };
  }, [form.plot_area, form.plot_unit, form.ground_coverage, form.floors, form.basement]);

  const canNext = () => {
    if (step === 1) return form.name.trim() && form.city.trim();
    if (step === 2) return form.plot_area && parseFloat(form.plot_area) > 0;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), city: form.city.trim(),
        plot_area: parseFloat(form.plot_area), plot_unit: form.plot_unit,
        ground_coverage: Number(form.ground_coverage), floors: Number(form.floors),
        basement: Number(form.basement), stage: form.stage,
        budget: form.budget ? parseFloat(form.budget) : null,
      };
      const res = await api.post("/projects", payload);
      setCreated(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not create project. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (created) return <Launchpad project={created} navigate={navigate} />;

  const STEP_TITLES = [
    "Let's start with your project.",
    "Tell us about your plot.",
    "Tell us about the building.",
    "Where are you currently?",
  ];

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8 lg:py-12">
        {/* progress */}
        <div className="flex items-center gap-2 mb-8" data-testid="wizard-progress">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-200">
              <div className={`h-full rounded-full transition-all duration-500 ${s <= step ? "bg-[#0E9AC8]" : "bg-transparent"}`} style={{ width: s <= step ? "100%" : "0%" }} />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Form column */}
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0E9AC8]">Step {step} of 4</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl tracking-tighter font-extrabold text-slate-900">{STEP_TITLES[step - 1]}</h1>

            <div className="mt-8 space-y-6 animate-fade-up" key={step}>
              {step === 1 && (
                <>
                  <div>
                    <Label className="flex items-center gap-1.5 text-slate-700"><Building2 className="h-4 w-4 text-slate-400" /> Project name</Label>
                    <Input data-testid="wizard-name" autoFocus className="rounded-xl mt-2 h-12" placeholder="e.g. My Dream Home"
                      value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5 text-slate-700"><MapPin className="h-4 w-4 text-slate-400" /> City</Label>
                    <Input data-testid="wizard-city" className="rounded-xl mt-2 h-12" placeholder="e.g. Ghaziabad"
                      value={form.city} onChange={(e) => set("city", e.target.value)} />
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div>
                    <Label className="flex items-center gap-1.5 text-slate-700"><LandPlot className="h-4 w-4 text-slate-400" /> Plot area</Label>
                    <div className="mt-2 flex gap-3">
                      <Input data-testid="wizard-plot-area" type="number" className="rounded-xl h-12 flex-1" placeholder="e.g. 200"
                        value={form.plot_area} onChange={(e) => set("plot_area", e.target.value)} />
                      <div className="flex rounded-xl border border-slate-200 bg-white p-1">
                        {Object.keys(UNIT_TO_SQFT).map((u) => (
                          <button key={u} type="button" data-testid={`wizard-unit-${u}`} onClick={() => set("plot_unit", u)}
                            className={`px-3 rounded-lg text-sm font-medium transition-colors ${form.plot_unit === u ? "bg-[#0E9AC8] text-white" : "text-slate-500 hover:text-slate-900"}`}>
                            {UNIT_LABEL[u]}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700">Ground coverage</Label>
                    <p className="text-xs text-slate-400 mt-0.5">How much of the plot the building footprint covers.</p>
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {COVERAGE.map((c) => (
                        <button key={c} type="button" data-testid={`wizard-coverage-${c}`} onClick={() => set("ground_coverage", c)}
                          className={`rounded-xl border py-3 text-sm font-semibold transition-colors ${form.ground_coverage === c ? "border-[#0E9AC8] bg-[#0E9AC8]/10 text-[#0E9AC8]" : "border-slate-200 text-slate-600 hover:border-[#0E9AC8]/50"}`}>
                          {c}%
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div>
                    <Label className="flex items-center gap-1.5 text-slate-700"><Layers className="h-4 w-4 text-slate-400" /> Number of floors</Label>
                    <div className="mt-3 grid grid-cols-5 gap-2">
                      {FLOORS.map((f) => (
                        <button key={f} type="button" data-testid={`wizard-floors-${f}`} onClick={() => set("floors", f)}
                          className={`rounded-xl border py-3 text-sm font-semibold transition-colors ${form.floors === f ? "border-[#0E9AC8] bg-[#0E9AC8]/10 text-[#0E9AC8]" : "border-slate-200 text-slate-600 hover:border-[#0E9AC8]/50"}`}>
                          {f === 5 ? "5+" : `G${f > 1 ? "+" + (f - 1) : ""}`}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-slate-400">Ground floor counts as 1 (G), so G+2 = 3 floors.</p>
                  </div>
                  <div>
                    <Label className="text-slate-700">Basement</Label>
                    <div className="mt-3 grid grid-cols-5 gap-2">
                      {BASEMENTS.map((b) => (
                        <button key={b} type="button" data-testid={`wizard-basement-${b}`} onClick={() => set("basement", b)}
                          className={`rounded-xl border py-3 text-sm font-semibold transition-colors ${form.basement === b ? "border-[#0E9AC8] bg-[#0E9AC8]/10 text-[#0E9AC8]" : "border-slate-200 text-slate-600 hover:border-[#0E9AC8]/50"}`}>
                          {b === 0 ? "None" : b}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {step === 4 && (
                <>
                  <div>
                    <Label className="text-slate-700">Current stage</Label>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[["new", "New Project", "Planning / not started"], ["under_construction", "Under Construction", "Work already in progress"]].map(([v, t, d]) => (
                        <button key={v} type="button" data-testid={`wizard-stage-${v}`} onClick={() => set("stage", v)}
                          className={`text-left rounded-xl border p-4 transition-colors ${form.stage === v ? "border-[#0E9AC8] bg-[#0E9AC8]/10" : "border-slate-200 hover:border-[#0E9AC8]/50"}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-slate-900">{t}</span>
                            {form.stage === v && <Check className="h-4 w-4 text-[#0E9AC8]" strokeWidth={3} />}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{d}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5 text-slate-700"><IndianRupee className="h-4 w-4 text-slate-400" /> Budget <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
                    <Input data-testid="wizard-budget" type="number" className="rounded-xl mt-2 h-12" placeholder="e.g. 8000000"
                      value={form.budget} onChange={(e) => set("budget", e.target.value)} />
                    <p className="mt-2 text-xs text-slate-400">Helps NCIS™ tailor cost guidance. You can add or change this later.</p>
                  </div>
                </>
              )}
            </div>

            {/* nav buttons */}
            <div className="mt-10 flex items-center gap-3">
              {step > 1 && (
                <Button variant="outline" data-testid="wizard-back" onClick={() => setStep((s) => s - 1)}
                  className="rounded-xl h-12 px-5 border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8]">
                  <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
                </Button>
              )}
              {step < 4 ? (
                <Button data-testid="wizard-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)}
                  className="rounded-xl h-12 px-8 bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white font-semibold disabled:opacity-40 transition-colors">
                  Next <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button data-testid="wizard-create" disabled={saving} onClick={submit}
                  className="rounded-xl h-12 px-8 bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white font-semibold transition-colors">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create My Project <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>
              )}
            </div>
          </div>

          {/* Live calculator column */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="rounded-3xl border border-[#0E9AC8]/20 bg-gradient-to-br from-[#0B1B2B] to-[#0e2c4d] text-white p-7 shadow-xl" data-testid="wizard-calculator">
              <div className="flex items-center gap-2 text-[#7dd3fc]">
                <Calculator className="h-5 w-5" strokeWidth={1.75} />
                <span className="text-xs font-semibold uppercase tracking-[0.16em]">Live Estimate</span>
              </div>
              <p className="mt-4 text-sm text-white/60">Estimated Construction Area</p>
              <p className="mt-1 font-display text-4xl font-extrabold tracking-tight text-white" data-testid="wizard-total-area">
                {calc ? fmtSqft(calc.total) : "—"}
              </p>
              <div className="mt-6 space-y-3 text-sm">
                {[
                  ["Plot area", calc ? fmtSqft(calc.plotSqft) : "—"],
                  [`Ground floor (${form.ground_coverage}%)`, calc ? fmtSqft(calc.gfa) : "—"],
                  [`Above ground (${form.floors} floor${form.floors > 1 ? "s" : ""})`, calc ? fmtSqft(calc.above) : "—"],
                  [`Basement (${form.basement || "none"})`, calc ? fmtSqft(calc.base) : "—"],
                ].map(([l, v]) => (
                  <div key={l} className="flex items-center justify-between border-b border-white/10 pb-2.5">
                    <span className="text-white/60">{l}</span>
                    <span className="font-semibold text-white">{v}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[11px] leading-relaxed text-white/45">
                This is an <b className="text-white/70">estimated construction area</b> for planning only — not sanctioned FAR/FSI/BUA.
                Actual permissible construction depends on applicable regulations, setbacks and sanctioned plans.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const STAGE_LABEL = { new: "New Project", under_construction: "Under Construction" };

function Launchpad({ project, navigate }) {
  const actions = [
    { icon: IndianRupee, title: "Get My Construction Estimate", desc: "Detailed cost estimate for your build.", to: `/project/${project.id}?tab=estimates`, testid: "launch-estimate" },
    { icon: FileStack, title: "Upload BOQ / Quotation / Drawing", desc: "Let NCIS™ analyse your documents.", to: `/project/${project.id}?tab=documents`, testid: "launch-upload" },
    { icon: MessageSquare, title: "Ask NCIS™", desc: "Chat about your project, anytime.", to: `/project/${project.id}?tab=chat`, testid: "launch-chat" },
    { icon: Users, title: "Find Relevant Vendors", desc: "Discover verified partners for your needs.", to: `/partners`, testid: "launch-vendors" },
  ];
  const summary = [
    ["City", project.city],
    ["Plot Area", `${project.plot_area} ${UNIT_LABEL[project.plot_unit] || project.plot_unit}`],
    ["Estimated Construction Area", project.est_construction_area ? `${project.est_construction_area.toLocaleString("en-IN")} Sq Ft` : "—"],
    ["Floors", project.floors],
    ["Basement", project.basement ? project.basement : "None"],
    ["Stage", STAGE_LABEL[project.stage] || project.stage],
  ];

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-12 animate-fade-up" data-testid="project-launchpad">
        <div className="text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="h-7 w-7 text-emerald-600" strokeWidth={1.75} />
          </div>
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#0E9AC8]">Your project is ready</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl tracking-tighter font-extrabold text-slate-900">{project.name}</h1>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 grid grid-cols-2 sm:grid-cols-3 gap-y-5 gap-x-4" data-testid="launchpad-summary">
          {summary.map(([l, v]) => (
            <div key={l}>
              <p className="text-xs text-slate-400">{l}</p>
              <p className="mt-0.5 font-semibold text-slate-900">{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <h2 className="font-display text-xl font-bold text-slate-900">What would you like to do next?</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {actions.map((a, i) => (
              <button key={a.title} data-testid={a.testid} onClick={() => navigate(a.to)}
                className="group text-left rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0E9AC8] hover:-translate-y-1 transition-all duration-200"
                style={{ animationDelay: `${i * 60}ms` }}>
                <div className="h-11 w-11 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center">
                  <a.icon className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.6} />
                </div>
                <h3 className="mt-4 font-semibold text-slate-900">{a.title}</h3>
                <p className="mt-1 text-sm text-slate-500">{a.desc}</p>
                <span className="mt-3 inline-flex items-center text-sm font-semibold text-[#0E9AC8] group-hover:gap-1.5 gap-1 transition-all">
                  Continue <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center">
          <button onClick={() => navigate(`/project/${project.id}`)} data-testid="launch-open-project"
            className="text-sm font-medium text-slate-500 hover:text-[#0E9AC8] transition-colors">
            Or open the full project workspace →
          </button>
        </div>
      </main>
    </div>
  );
}
